import type { JSX } from "react";
import { useMemo } from "react";

import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import { VerticalLegendsWithSearch } from "app/components/React/legend/vertical-legends-with-search/VerticalLegendsWithSearch";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { COMMON_COLORS } from "../../common/commonColors";

import { LegendsWrapper } from "./StyledComponent";
import {
    DEFAULT_UN_CHECK_COLOR,
    MAX_SELECTION,
    MIN_DOMAINS_FOR_OTHERS_DISPLAY,
    getOthersDomainName,
} from "./utils/const";

const availableColors = []; // support FIFO policy for colors

interface IMarketSharesChartLegendsProps {
    chartDisplayType: EChartDisplayType;
    legendItems: ILegendItems;
    getLegendColor: () => string;
    onLegendsChange: (legendItems: ILegendItems) => void;
    isDaily: boolean;
    disableOthers?: boolean;
}

export const MarketSharesChartLegends = ({
    chartDisplayType,
    legendItems,
    getLegendColor,
    onLegendsChange,
    isDaily,
    disableOthers = false,
}: IMarketSharesChartLegendsProps): JSX.Element => {
    const isPercentage = chartDisplayType === EChartDisplayType.PERCENTAGE;
    const othersDomainName = getOthersDomainName();
    const otherLegend = isPercentage
        ? legendItems
              .filter(({ name }) => name === othersDomainName)
              .map((item) => ({ ...item, data: null, color: COMMON_COLORS.OTHER, isDisabled: disableOthers }))
              .at(0)
        : undefined;
    const legendItemsWithoutOther = legendItems.filter(({ name }) => name !== othersDomainName);
    const shouldShowOtherLegend = isPercentage && legendItemsWithoutOther.length >= MIN_DOMAINS_FOR_OTHERS_DISPLAY;

    const toggleSeries = (clickedItem) => {
        if (clickedItem.name === othersDomainName && disableOthers) {
            return;
        }

        const eventName = chartDisplayType === EChartDisplayType.ABSOLUTE ? "Absolute" : "Share";
        allTrackers.dispatchTrackEvent(
            "Click Filter",
            clickedItem.visible ? "uncheck" : "check",
            `${eventName}/${clickedItem.name}`,
        );

        const newLegendItems = legendItems.map((legendItem) => {
            const isOthers = legendItem.name === othersDomainName;

            if (legendItem.id !== clickedItem.id) {
                return legendItem;
            }

            if (isOthers) {
                legendItem.visible = !clickedItem.visible;
                legendItem.color = legendItem.visible ? COMMON_COLORS.OTHER : DEFAULT_UN_CHECK_COLOR;
                return legendItem;
            }

            const visibleLegendItems = legendItems.filter(({ visible, name }) => visible && name !== othersDomainName);

            legendItem.visible = !clickedItem.visible && visibleLegendItems.length < MAX_SELECTION;

            if (legendItem.visible) {
                legendItem.color = availableColors.length ? availableColors.pop() : getLegendColor();
            } else {
                availableColors.push(legendItem.color);
                legendItem.color = DEFAULT_UN_CHECK_COLOR;
            }

            return legendItem;
        });

        onLegendsChange(newLegendItems);
    };

    const text = useMemo(
        () => ({
            searchPlaceHolder: translate("common.placeholder.search.domain"),
            searchLabel: translate("common.domains.selected"),
        }),
        [],
    );

    return (
        <LegendsWrapper data-automation="market-shares-chart-legends">
            <VerticalLegendsWithSearch
                legendItems={legendItemsWithoutOther}
                toggleSeries={toggleSeries}
                maxSelection={MAX_SELECTION}
                legendItemsHeight={isPercentage ? "200px" : "220px"}
                text={text}
                titles={[
                    translate("common.domain"),
                    translate(
                        isPercentage
                            ? "common.tables.columns.traffic.share.title"
                            : isDaily
                              ? "common.tables.columns.avg.daily.visits.title"
                              : "common.tables.columns.monthly.visits.title",
                    ),
                ]}
                otherLegend={shouldShowOtherLegend ? otherLegend : null}
                textMaxWidth={"90px"}
                subtitleMaxWidth={"90px"}
            />
        </LegendsWrapper>
    );
};
